import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from '../config';
import { ErrorState } from '../components/ErrorState';
import { classifyError } from '../lib/apiError';
import {
  Zap, Droplets, Users, Plus, FileText, Loader2, Home, Wallet, ChevronDown, ChevronUp
} from "lucide-react";

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function OrganizationDashboardPage() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetails, setOrgDetails] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [groups, setGroups] = useState([]);
  const [pendingSplits, setPendingSplits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [showBusiness, setShowBusiness] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showAddMeter, setShowAddMeter] = useState(false);
  const [showChequeRequest, setShowChequeRequest] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddGroupMeter, setShowAddGroupMeter] = useState(false);
  const [showCreateSplit, setShowCreateSplit] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [registerForm, setRegisterForm] = useState({
    name: "", registration_number: "", tax_id: "", address: "", city: "",
    contact_person: "", contact_phone: "", contact_email: "", bank_name: "", bank_account: ""
  });
  const [meterForm, setMeterForm] = useState({ meter_number: "", provider_code: "ESCOM", meter_type: "electricity", location_name: "", address: "" });
  const [chequeForm, setChequeForm] = useState({ meters: [], cheque_number: "", cheque_date: "", bank_name: "", notes: "" });
  const [selectedMetersForPayment, setSelectedMetersForPayment] = useState([]);
  const [accountForm, setAccountForm] = useState({ provider_code: "", account_number: "", nickname: "" });
  const [groupForm, setGroupForm] = useState({ name: "", group_type: "household", description: "", split_method: "equal" });
  const [memberForm, setMemberForm] = useState({ name: "", phone: "", share_percentage: "" });
  const [groupMeterForm, setGroupMeterForm] = useState({ meter_number: "", provider_code: "ESCOM", name: "" });
  const [splitForm, setSplitForm] = useState({ meter_number: "", provider_code: "ESCOM", total_amount: "", due_date: "" });

  useEffect(() => { fetchHousehold(); }, []);

  const fetchHousehold = async () => {
    setLoading(true);
    setError(null);
    try {
      const [accountsRes, groupsRes, pendingRes, providersRes, orgsRes] = await Promise.allSettled([
        api.get("/accounts"),
        api.get("/groups"),
        api.get("/split-bills/my-pending"),
        api.get("/providers"),
        api.get("/organizations/my"),
      ]);

      const firstFailed = [accountsRes, groupsRes, providersRes].find((r) => r.status === "rejected");
      if (firstFailed) throw firstFailed.reason;

      setAccounts(accountsRes.value.data || []);
      setGroups(groupsRes.value.data || []);
      setPendingSplits(pendingRes.status === "fulfilled" ? (pendingRes.value.data || []) : []);
      setProviders(providersRes.value.data || []);

      if (orgsRes.status === "fulfilled") {
        const orgs = orgsRes.value.data || [];
        setOrganizations(orgs);
        if (orgs.length > 0) {
          setShowBusiness(true);
          setSelectedOrg(orgs[0]);
          fetchOrgDetails(orgs[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to load family hub:", err);
      const classified = classifyError(err);
      setError(classified);
      toast.error(classified.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgDetails = async (orgId) => {
    setDetailError(null);
    try {
      const response = await api.get(`/organizations/${orgId}`);
      setOrgDetails(response.data);
    } catch (err) {
      console.error("Failed to load organization details:", err);
      const classified = classifyError(err);
      setDetailError(classified);
      toast.error(classified.message);
    }
  };

  const payForAccount = (account) => {
    const params = new URLSearchParams({
      provider: account.provider_code,
      meter: account.account_number,
    });
    navigate(`/buy?${params.toString()}`);
  };

  const saveHouseholdMeter = async () => {
    if (!accountForm.provider_code || !accountForm.account_number) {
      toast.error("Provider and meter number are required");
      return;
    }
    try {
      await api.post("/accounts", {
        provider_code: accountForm.provider_code,
        account_number: accountForm.account_number,
        nickname: accountForm.nickname || accountForm.account_number,
      });
      toast.success("Meter saved");
      setShowAddAccount(false);
      setAccountForm({ provider_code: "", account_number: "", nickname: "" });
      fetchHousehold();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to save meter");
    }
  };

  const createGroup = async () => {
    if (!groupForm.name) {
      toast.error("Give the household a name");
      return;
    }
    try {
      await api.post("/groups", null, { params: groupForm });
      toast.success("Household group created");
      setShowAddGroup(false);
      setGroupForm({ name: "", group_type: "household", description: "", split_method: "equal" });
      fetchHousehold();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to create group");
    }
  };

  const openAddMember = (groupId) => {
    setActiveGroupId(groupId);
    setMemberForm({ name: "", phone: "", share_percentage: "" });
    setShowAddMember(true);
  };

  const openAddGroupMeter = (groupId) => {
    setActiveGroupId(groupId);
    setGroupMeterForm({ meter_number: "", provider_code: "ESCOM", name: "" });
    setShowAddGroupMeter(true);
  };

  const openCreateSplit = (groupId) => {
    const group = groups.find((g) => g.id === groupId);
    const firstMeter = group?.meters?.[0];
    setActiveGroupId(groupId);
    setSplitForm({
      meter_number: firstMeter?.meter_number || "",
      provider_code: firstMeter?.provider_code || "ESCOM",
      total_amount: "",
      due_date: "",
    });
    setShowCreateSplit(true);
  };

  const addGroupMember = async () => {
    if (!memberForm.name || !memberForm.phone) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      const params = { name: memberForm.name, phone: memberForm.phone };
      if (memberForm.share_percentage !== "") params.share_percentage = parseFloat(memberForm.share_percentage);
      await api.post(`/groups/${activeGroupId}/members`, null, { params });
      toast.success("Person added");
      setShowAddMember(false);
      fetchHousehold();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to add person");
    }
  };

  const addGroupMeter = async () => {
    if (!groupMeterForm.meter_number || !groupMeterForm.name) {
      toast.error("Meter number and name are required");
      return;
    }
    try {
      await api.post(`/groups/${activeGroupId}/meters`, null, { params: groupMeterForm });
      toast.success("Meter added to the group");
      setShowAddGroupMeter(false);
      fetchHousehold();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to add meter");
    }
  };

  const createSplitBill = async () => {
    if (!splitForm.meter_number || !splitForm.total_amount) {
      toast.error("Meter number and amount are required");
      return;
    }
    try {
      const params = {
        meter_number: splitForm.meter_number,
        provider_code: splitForm.provider_code,
        total_amount: parseFloat(splitForm.total_amount),
      };
      if (splitForm.due_date) params.due_date = splitForm.due_date;
      await api.post(`/groups/${activeGroupId}/contributions`, null, { params });
      toast.success("Split created — everyone in the group can chip in");
      setShowCreateSplit(false);
      fetchHousehold();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to create split");
    }
  };

  const contributePending = async (bill) => {
    const amount = bill.my_remaining;
    if (!amount) return;
    try {
      await api.post(`/split-bills/bills/${bill.bill_id}/contribute`, { amount });
      toast.success("Your share is paid from wallet");
      fetchHousehold();
    } catch (err) {
      toast.error(classifyError(err).message || "Could not pay share — top up your wallet first");
    }
  };

  const registerOrganization = async () => {
    try {
      await api.post("/organizations", registerForm);
      toast.success("Organization registered successfully! Pending admin approval.");
      setShowRegister(false);
      fetchHousehold();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to register organization");
    }
  };

  const addMeter = async () => {
    try {
      await api.post(`/organizations/${selectedOrg.id}/meters`, meterForm);
      toast.success("Meter added successfully");
      setShowAddMeter(false);
      fetchOrgDetails(selectedOrg.id);
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to add meter");
    }
  };

  const submitChequeRequest = async () => {
    try {
      const metersData = selectedMetersForPayment.map(m => ({
        meter_number: m.meter_number,
        provider_code: m.provider_code,
        amount: m.paymentAmount || 0
      }));
      await api.post(`/organizations/${selectedOrg.id}/cheque-payment`, {
        meters: metersData,
        cheque_number: chequeForm.cheque_number,
        cheque_date: chequeForm.cheque_date,
        bank_name: chequeForm.bank_name,
        notes: chequeForm.notes
      });
      toast.success("Cheque payment request submitted successfully");
      setShowChequeRequest(false);
      setSelectedMetersForPayment([]);
      fetchOrgDetails(selectedOrg.id);
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to submit request");
    }
  };

  const toggleMeterForPayment = (meter) => {
    setSelectedMetersForPayment(prev => {
      const exists = prev.find(m => m.meter_number === meter.meter_number);
      if (exists) {
        return prev.filter(m => m.meter_number !== meter.meter_number);
      }
      return [...prev, { ...meter, paymentAmount: 0 }];
    });
  };

  const updateMeterPaymentAmount = (meterNumber, amount) => {
    setSelectedMetersForPayment(prev => prev.map(m => m.meter_number === meterNumber ? { ...m, paymentAmount: parseFloat(amount) || 0 } : m));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchHousehold} />;
  }

  const totalChequeAmount = selectedMetersForPayment.reduce((sum, m) => sum + (m.paymentAmount || 0), 0);
  const activeGroup = groups.find((g) => g.id === activeGroupId);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="organization-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Family & teams</h1>
        <p className="text-muted-foreground">Pay Mum’s meter, split with roommates, or cover the whole house — no company registration needed.</p>
      </div>

      {pendingSplits.length > 0 && (
        <div className="bg-card border border-amber-500/30 rounded-xl p-4">
          <h2 className="font-semibold mb-3">Your share due</h2>
          <div className="space-y-2">
            {pendingSplits.map((bill) => (
              <div key={bill.bill_id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border/50">
                <div>
                  <p className="font-medium text-sm">{bill.group_name || "Split bill"}</p>
                  <p className="text-xs text-muted-foreground">{bill.provider_code} · remaining MWK {(bill.my_remaining || 0).toLocaleString()}</p>
                </div>
                <button onClick={() => contributePending(bill)} className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600">
                  Pay share
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border/50 rounded-xl p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold flex items-center gap-2"><Home className="w-5 h-5 text-emerald-500" /> People & meters you pay for</h2>
          <button onClick={() => setShowAddAccount(true)} className="flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-lg text-sm" data-testid="add-family-meter-btn">
            <Plus className="w-4 h-4" /> Add meter
          </button>
        </div>
        {accounts.length === 0 ? (
          <div className="text-center py-8">
            <Home className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No household meters yet</p>
            <p className="text-sm text-muted-foreground mb-4">Save Mum’s ESCOM, the plot water account, or a rental meter and pay them in one tap.</p>
            <button onClick={() => setShowAddAccount(true)} className="px-4 py-2 bg-emerald-500 text-white rounded-lg">Add a family meter</button>
          </div>
        ) : (
          <div className="space-y-2">
            {accounts.map((account) => (
              <div key={account.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border/50">
                <div className="min-w-0">
                  <p className="font-medium truncate">{account.nickname || "Unnamed meter"}</p>
                  <p className="text-xs text-muted-foreground">{account.provider_code} · {account.account_number}</p>
                </div>
                <button onClick={() => payForAccount(account)} className="shrink-0 flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm">
                  <Wallet className="w-4 h-4" /> Pay now
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card border border-border/50 rounded-xl p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold flex items-center gap-2"><Users className="w-5 h-5 text-blue-500" /> Household groups</h2>
          <button onClick={() => setShowAddGroup(true)} className="flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-lg text-sm" data-testid="create-household-group-btn">
            <Plus className="w-4 h-4" /> Start a group
          </button>
        </div>
        {groups.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">Split bills with family or roommates</p>
            <p className="text-sm text-muted-foreground">Create a group, add people by phone, then split a meter when the bill is due.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => (
              <div key={group.id} className="p-4 bg-muted/30 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold">{group.name}</p>
                    <p className="text-xs text-muted-foreground">{group.group_type} · {group.members?.length || 0} people · {group.split_method} split</p>
                  </div>
                </div>
                {group.description && <p className="text-sm text-muted-foreground mb-2">{group.description}</p>}
                {group.meters?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {group.meters.map((m, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500">{m.name} · {m.meter_number}</span>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => openAddMember(group.id)} className="text-xs px-2 py-1 bg-muted rounded hover:bg-muted/70">Add person</button>
                  <button onClick={() => openAddGroupMeter(group.id)} className="text-xs px-2 py-1 bg-muted rounded hover:bg-muted/70">Add meter</button>
                  <button onClick={() => openCreateSplit(group.id)} className="text-xs px-2 py-1 bg-blue-500/20 text-blue-500 rounded hover:bg-blue-500/30">Split a bill</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card border border-border/50 rounded-xl p-4">
        <button
          onClick={() => setShowBusiness((v) => !v)}
          className="w-full flex items-center justify-between text-left"
          data-testid="toggle-business-section"
        >
          <div>
            <h2 className="font-semibold">Business / organisation</h2>
            <p className="text-sm text-muted-foreground">Cheque bulk pay for a shop, school, or company — optional.</p>
          </div>
          {showBusiness ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
        </button>

        {showBusiness && (
          <div className="mt-4 space-y-4">
            {organizations.length === 0 ? (
              <div className="text-center py-6 border border-dashed border-border rounded-lg">
                <p className="text-sm text-muted-foreground mb-3">Most households skip this. Use it only if you need official cheque payments.</p>
                <button onClick={() => setShowRegister(true)} className="px-4 py-2 bg-muted rounded-lg hover:bg-muted/80" data-testid="register-org-btn">
                  Register organisation
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-muted/20 border border-border/50 rounded-xl p-4">
                  <h3 className="font-semibold mb-4">Your organisations</h3>
                  <div className="space-y-2">
                    {organizations.map((org) => (
                      <div key={org.id} onClick={() => { setSelectedOrg(org); fetchOrgDetails(org.id); }}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedOrg?.id === org.id ? "border-emerald-500 bg-emerald-500/10" : "border-border/50 hover:bg-muted/50"}`}
                        data-testid={`org-select-${org.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{org.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${org.status === "approved" ? "bg-emerald-500/20 text-emerald-500" : org.status === "pending" ? "bg-yellow-500/20 text-yellow-500" : "bg-red-500/20 text-red-500"}`}>
                            {org.status}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{org.registration_number}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-2 space-y-4">
                  {selectedOrg && orgDetails ? (
                    <>
                      <div className="border border-border/50 rounded-xl p-4">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">{orgDetails.organization?.name}</h3>
                            <p className="text-sm text-muted-foreground">{orgDetails.organization?.address}, {orgDetails.organization?.city}</p>
                          </div>
                          {orgDetails.organization?.status === "approved" && (
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Credit Limit</p>
                              <p className="font-semibold">MWK {(orgDetails.organization?.credit_limit || 0).toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Contact</p>
                            <p className="font-medium">{orgDetails.organization?.contact_person}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Phone</p>
                            <p className="font-medium">{orgDetails.organization?.contact_phone}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Meters</p>
                            <p className="font-medium">{orgDetails.meters?.length || 0}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Pending Requests</p>
                            <p className="font-medium">{orgDetails.pending_requests?.length || 0}</p>
                          </div>
                        </div>
                      </div>

                      <div className="border border-border/50 rounded-xl p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="font-semibold">Organisation meters</h3>
                          <div className="flex gap-2">
                            {selectedMetersForPayment.length > 0 && (
                              <button onClick={() => setShowChequeRequest(true)} className="flex items-center gap-1 px-3 py-1 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-all">
                                <FileText className="w-4 h-4" /> Create Cheque Request ({selectedMetersForPayment.length})
                              </button>
                            )}
                            <button onClick={() => setShowAddMeter(true)} className="flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600 transition-all" data-testid="add-meter-btn">
                              <Plus className="w-4 h-4" /> Add Meter
                            </button>
                          </div>
                        </div>
                        {orgDetails.meters?.length === 0 ? (
                          <p className="text-muted-foreground text-sm">No meters added yet</p>
                        ) : (
                          <div className="space-y-2">
                            {orgDetails.meters?.map((meter) => {
                              const isSelected = selectedMetersForPayment.find(m => m.meter_number === meter.meter_number);
                              return (
                                <div key={meter.id} className={`flex items-center justify-between p-3 rounded-lg border transition-all ${isSelected ? "border-blue-500 bg-blue-500/10" : "border-border/50 hover:bg-muted/50"}`}>
                                  <div className="flex items-center gap-3">
                                    <input type="checkbox" checked={!!isSelected} onChange={() => toggleMeterForPayment(meter)} className="w-4 h-4 rounded" />
                                    <div className={`p-2 rounded-lg ${meter.meter_type === "electricity" ? "bg-yellow-500/10" : "bg-blue-500/10"}`}>
                                      {meter.meter_type === "electricity" ? <Zap className="w-4 h-4 text-yellow-500" /> : <Droplets className="w-4 h-4 text-blue-500" />}
                                    </div>
                                    <div>
                                      <p className="font-medium text-sm">{meter.meter_number}</p>
                                      <p className="text-xs text-muted-foreground">{meter.location_name}</p>
                                    </div>
                                  </div>
                                  <span className="text-xs text-muted-foreground">{meter.provider_code}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {orgDetails.pending_requests?.length > 0 && (
                        <div className="border border-border/50 rounded-xl p-4">
                          <h3 className="font-semibold mb-4">Pending Cheque Requests</h3>
                          <div className="space-y-2">
                            {orgDetails.pending_requests.map((req) => (
                              <div key={req.id} className="flex items-center justify-between p-3 rounded-lg border border-border/50">
                                <div>
                                  <p className="font-medium text-sm">{req.request_number}</p>
                                  <p className="text-xs text-muted-foreground">{req.meters?.length} meters - MWK {req.total_amount.toLocaleString()}</p>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${req.status === "approved" ? "bg-emerald-500/20 text-emerald-500" : req.status === "processing" ? "bg-blue-500/20 text-blue-500" : "bg-yellow-500/20 text-yellow-500"}`}>
                                  {req.status}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : detailError ? (
                    <div className="border border-border/50 rounded-xl">
                      <ErrorState error={detailError} onRetry={() => fetchOrgDetails(selectedOrg.id)} />
                    </div>
                  ) : (
                    <div className="border border-border/50 rounded-xl p-8 text-center">
                      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Select an organisation to view details</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showAddAccount && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Add a family meter</h3>
              <div className="space-y-4">
                <select value={accountForm.provider_code} onChange={(e) => setAccountForm({ ...accountForm, provider_code: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg">
                  <option value="">Select provider</option>
                  {providers.map((p) => <option key={p.code} value={p.code}>{p.name || p.code}</option>)}
                </select>
                <input placeholder="Meter / account number" value={accountForm.account_number} onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <input placeholder="Nickname (e.g. Mum’s house)" value={accountForm.nickname} onChange={(e) => setAccountForm({ ...accountForm, nickname: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddAccount(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={saveHouseholdMeter} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg">Save meter</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddGroup && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Start a household group</h3>
              <div className="space-y-4">
                <input placeholder="Group name (e.g. Area 47 house)" value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <select value={groupForm.group_type} onChange={(e) => setGroupForm({ ...groupForm, group_type: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg">
                  <option value="household">Household</option>
                  <option value="roommates">Roommates</option>
                  <option value="community">Community</option>
                  <option value="business">Business</option>
                </select>
                <select value={groupForm.split_method} onChange={(e) => setGroupForm({ ...groupForm, split_method: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg">
                  <option value="equal">Split equally</option>
                  <option value="percentage">By percentage</option>
                </select>
                <textarea placeholder="Description (optional)" value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" rows={2} />
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddGroup(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={createGroup} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg">Create group</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddMember && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Add a person</h3>
              <div className="space-y-4">
                <input placeholder="Name" value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <input placeholder="Phone number" value={memberForm.phone} onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <input type="number" placeholder="Share % (optional)" value={memberForm.share_percentage} onChange={(e) => setMemberForm({ ...memberForm, share_percentage: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddMember(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={addGroupMember} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg">Add person</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddGroupMeter && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Add a meter to the group</h3>
              <div className="space-y-4">
                <input placeholder="Name (e.g. House electricity)" value={groupMeterForm.name} onChange={(e) => setGroupMeterForm({ ...groupMeterForm, name: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <input placeholder="Meter number" value={groupMeterForm.meter_number} onChange={(e) => setGroupMeterForm({ ...groupMeterForm, meter_number: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <select value={groupMeterForm.provider_code} onChange={(e) => setGroupMeterForm({ ...groupMeterForm, provider_code: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg">
                  <option value="ESCOM">ESCOM (Electricity)</option>
                  <option value="LWB">Lilongwe Water Board</option>
                  <option value="BWB">Blantyre Water Board</option>
                  <option value="NRWB">Northern Region Water Board</option>
                </select>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddGroupMeter(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={addGroupMeter} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg">Add meter</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreateSplit && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Split a bill</h3>
              {(activeGroup?.meters || []).length > 0 ? (
                <div className="space-y-4">
                  <select
                    value={`${splitForm.meter_number}|${splitForm.provider_code}`}
                    onChange={(e) => {
                      const [meter_number, provider_code] = e.target.value.split("|");
                      setSplitForm({ ...splitForm, meter_number, provider_code });
                    }}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg">
                    {(activeGroup?.meters || []).map((m, i) => (
                      <option key={i} value={`${m.meter_number}|${m.provider_code}`}>{m.name} · {m.meter_number}</option>
                    ))}
                  </select>
                  <input type="number" placeholder="Total amount (MWK)" value={splitForm.total_amount} onChange={(e) => setSplitForm({ ...splitForm, total_amount: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                  <input type="date" value={splitForm.due_date} onChange={(e) => setSplitForm({ ...splitForm, due_date: e.target.value })} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                  <p className="text-xs text-muted-foreground">Split among {activeGroup?.members?.length || 0} people using {activeGroup?.split_method} shares.</p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Add a meter to this group first.</p>
              )}
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowCreateSplit(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={createSplitBill} disabled={!(activeGroup?.meters?.length)} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg disabled:opacity-50">Create split</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRegister && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-2xl w-full my-8">
              <h3 className="text-lg font-semibold mb-4">Register Organisation</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Organization Name *</label>
                  <input type="text" value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="Company Ltd" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Registration Number *</label>
                  <input type="text" value={registerForm.registration_number} onChange={(e) => setRegisterForm({ ...registerForm, registration_number: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="REG-12345" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tax ID</label>
                  <input type="text" value={registerForm.tax_id} onChange={(e) => setRegisterForm({ ...registerForm, tax_id: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="TAX-12345" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <input type="text" value={registerForm.city} onChange={(e) => setRegisterForm({ ...registerForm, city: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="Lilongwe" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Address *</label>
                  <input type="text" value={registerForm.address} onChange={(e) => setRegisterForm({ ...registerForm, address: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="123 Business Street" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Person *</label>
                  <input type="text" value={registerForm.contact_person} onChange={(e) => setRegisterForm({ ...registerForm, contact_person: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="John Doe" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Phone *</label>
                  <input type="text" value={registerForm.contact_phone} onChange={(e) => setRegisterForm({ ...registerForm, contact_phone: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="0999123456" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Email *</label>
                  <input type="email" value={registerForm.contact_email} onChange={(e) => setRegisterForm({ ...registerForm, contact_email: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="finance@company.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bank Name</label>
                  <input type="text" value={registerForm.bank_name} onChange={(e) => setRegisterForm({ ...registerForm, bank_name: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="National Bank" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bank Account</label>
                  <input type="text" value={registerForm.bank_account} onChange={(e) => setRegisterForm({ ...registerForm, bank_account: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="1234567890" />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowRegister(false)} className="flex-1 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-all">Cancel</button>
                <button onClick={registerOrganization} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all">Register</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddMeter && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Add Meter</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Meter Number</label>
                  <input type="text" value={meterForm.meter_number} onChange={(e) => setMeterForm({ ...meterForm, meter_number: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Provider</label>
                  <select value={meterForm.provider_code} onChange={(e) => setMeterForm({ ...meterForm, provider_code: e.target.value, meter_type: e.target.value.includes("ESCOM") ? "electricity" : "water" })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg">
                    <option value="ESCOM">ESCOM (Electricity)</option>
                    <option value="LLWB">Lilongwe Water Board</option>
                    <option value="BWB">Blantyre Water Board</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Location Name</label>
                  <input type="text" value={meterForm.location_name} onChange={(e) => setMeterForm({ ...meterForm, location_name: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="Head Office" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <input type="text" value={meterForm.address} onChange={(e) => setMeterForm({ ...meterForm, address: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="Building address" />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddMeter(false)} className="flex-1 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-all">Cancel</button>
                <button onClick={addMeter} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all">Add Meter</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showChequeRequest && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-2xl w-full my-8">
              <h3 className="text-lg font-semibold mb-4">Create Cheque Payment Request</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Meters to Pay</label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedMetersForPayment.map((meter) => (
                    <div key={meter.meter_number} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        {meter.meter_type === "electricity" ? <Zap className="w-4 h-4 text-yellow-500" /> : <Droplets className="w-4 h-4 text-blue-500" />}
                        <div>
                          <p className="text-sm font-medium">{meter.meter_number}</p>
                          <p className="text-xs text-muted-foreground">{meter.location_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">MWK</span>
                        <input type="number" value={meter.paymentAmount || ""} onChange={(e) => updateMeterPaymentAmount(meter.meter_number, e.target.value)}
                          className="w-32 p-1 bg-background border border-border/50 rounded text-right" placeholder="0" />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-right">
                  <span className="font-semibold">Total: MWK {totalChequeAmount.toLocaleString()}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Cheque Number</label>
                  <input type="text" value={chequeForm.cheque_number} onChange={(e) => setChequeForm({ ...chequeForm, cheque_number: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="CHQ-001234" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Cheque Date</label>
                  <input type="date" value={chequeForm.cheque_date} onChange={(e) => setChequeForm({ ...chequeForm, cheque_date: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bank Name</label>
                  <input type="text" value={chequeForm.bank_name} onChange={(e) => setChequeForm({ ...chequeForm, bank_name: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" placeholder="National Bank" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea value={chequeForm.notes} onChange={(e) => setChequeForm({ ...chequeForm, notes: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" rows={2} placeholder="Additional notes..." />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowChequeRequest(false)} className="flex-1 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-all">Cancel</button>
                <button onClick={submitChequeRequest} disabled={totalChequeAmount === 0} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50">
                  Submit Request
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
